import { useEffect } from 'react'

/**
 * Stop unsaved work from disappearing.
 *
 * There is no unsaved-changes protection anywhere else in this dashboard, and
 * the billing console is the screen where its absence costs most: an operator
 * can have a morning of weights typed into two hundred rows before the first
 * save.
 *
 * WHY NOT `useBlocker`. React Router ships one, and it is the right tool - but
 * it requires a DATA router, and this app mounts `<BrowserRouter>`. Calling it
 * here throws outright ("useBlocker must be used within a data router"), and
 * migrating the whole app to `createBrowserRouter` is not a change this screen
 * should be making. So navigation is guarded the way it can be without touching
 * the router:
 *
 *   1. `beforeunload` covers closing the tab, reloading, and the address bar.
 *   2. A capture-phase click listener covers in-app links - the sidebar being
 *      the one that matters, since that is how an operator actually leaves.
 *
 * The click listener runs in the CAPTURE phase so it sees the event before
 * React Router's own handler turns it into a navigation.
 */
export function useUnsavedChangesGuard(dirty: boolean, message: string): void {
  useEffect(() => {
    if (!dirty) return undefined

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      // Browsers ignore custom text now and show their own wording; assigning
      // returnValue is still what triggers the prompt at all.
      event.preventDefault()
      event.returnValue = ''
      return ''
    }

    const onClickCapture = (event: MouseEvent) => {
      // Let modified clicks through - they open a new tab and leave this one
      // (and its unsaved work) exactly where it is.
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const anchor = (event.target as HTMLElement | null)?.closest('a')
      if (!anchor) return

      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#') || anchor.target === '_blank') return

      if (!window.confirm(message)) {
        event.preventDefault()
        event.stopPropagation()
      }
    }

    window.addEventListener('beforeunload', onBeforeUnload)
    document.addEventListener('click', onClickCapture, true)

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload)
      document.removeEventListener('click', onClickCapture, true)
    }
  }, [dirty, message])
}
